import React, { useCallback, useEffect, useState } from "react";

import { ReactComponent as GmailIcon } from "../../../imgs/icons/gmail.svg";
import { ReactComponent as IntercomIcon } from "../../../imgs/icons/intercom.svg";
import { ReactComponent as UploadFileIcon } from "../../../imgs/icons/uploadFile.svg";
import { ReactComponent as WebsiteSitemapIcon } from "../../../imgs/icons/chrome.svg";

import api from "../../../api.js";

import AddSourceModal from "./AddSourceModal.jsx";
import AddWebsiteModal from "./AddWebsiteModal.jsx";
import AddQsAndAsModal from "./AddQsAndAsModal.jsx";
import AddPoliciesModal from "./AddPoliciesModal.jsx";
import AddGmailModal from "./AddGmailModal.jsx";
import AddIntercomModal from "./AddIntercomModal.jsx";
import AddUploadedFiles from "./AddUploadedFiles.jsx";
import { PaginationRow } from "../../../components/PaginationRow.jsx";
import CenterWrapper from "../../../components/CenterWrapper.jsx";
import KnowledgeSourceRow from "../../../components/KnowledgeSourceRow";
import MOCK_UPLOADS from "../../../mock/uploads";
import SectionHeader from "../../../components/SectionHeader";
import GlobalLoadingSpinner from "../../../components/GlobalSpinner";
import PrimaryButton from "../../../components/PrimaryButton";
import { PlusIcon } from "@heroicons/react/24/outline";
import GeneralSetupTab from "../../../components/GeneralSetupTab";

const KnowledgeSources = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddWebsiteModalVisible, setIsAddWebsiteModalVisible] =
    useState(false);

  const [isQsAndAsModalVisible, setIsQsAndAsModalVisible] = useState(false);
  const [isPoliciesModalVisible, setIsPoliciesModalVisible] = useState(false);
  const [isGmailModalVisible, setIsGmailModalVisible] = useState(false);
  const [isIntercomModalVisible, setIsIntercomModalVisible] = useState(false);
  const [isUploadFilesModalVisible, setIsUploadFilesModalVisible] =
    useState(false);

  const steps = {
    website: () => setIsAddWebsiteModalVisible(true),
    custom: () => setIsQsAndAsModalVisible(true),
    policies: () => setIsPoliciesModalVisible(true),
    gmail: () => setIsGmailModalVisible(true),
    intercom: () => setIsIntercomModalVisible(true),
    files: () => setIsUploadFilesModalVisible(true),
  };

  const [loading, setLoading] = useState(true);
  const [knowledgeSources, setKnowledgeSources] = useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [totalPages, setTotalPages] = useState(1);

  const removeSource = async id => {
    try {
      setLoading(true);

      await api.delete(`/api/uploads/${id}`);

      fetchKnowledgeSources();
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const disableSource = async source_id => {
    setLoading(true);
    try {
      await api.put(`/api/uploads/${source_id}`, {
        is_live: false,
      });

      fetchKnowledgeSources();
    } catch (error) {
      console.error("Error updating source:", error);
    }
  };

  const enableSource = async source_id => {
    setLoading(true);
    try {
      await api.put(`/api/uploads/${source_id}`, {
        is_live: true,
      });

      fetchKnowledgeSources();
    } catch (error) {
      console.error("Error updating source:", error);
    }
  };

  const fetchKnowledgeSources = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/uploads`, {
        params: {
          chatbot_uuid: localStorage.getItem("chatbot_uuid"),
          page,
          page_size: pageSize,
        },
      });

      setKnowledgeSources(response.data.uploads);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Error fetchings uploaded sources:", error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchKnowledgeSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const renderIcon = type => {
    const icons = {
      GMAIL: <GmailIcon className="w-6 h-6 mr-2 text-red-500" />,
      INTERCOM: <IntercomIcon className="w-6 h-6 mr-2 text-blue-500" />,
      TEXT: <UploadFileIcon className="w-6 h-6 mr-2 text-gray-500" />,
      FILE: <UploadFileIcon className="w-6 h-6 mr-2 text-gray-500" />,
      URL: <WebsiteSitemapIcon className="w-6 h-6 mr-2 text-green-500" />,
    };
    return icons[type] || null;
  };

  const renderTitle = type => {
    const titles = {
      GMAIL: "Gmail",
      INTERCOM: "Intercom",
      TEXT: "Document Upload",
      FILE: "Document Upload",
      URL: "Website",
    };
    return titles[type] || null;
  };

  const handleCheckboxChange = id => {
    setCheckBoxes(prev => {
      const newCheckBoxes = { ...prev, [id]: !prev[id] };
      if (!newCheckBoxes[id]) {
        newCheckBoxes.all = false;
      }
      return newCheckBoxes;
    });
  };

  const [checkBoxes, setCheckBoxes] = useState(
    knowledgeSources.reduce(
      (acc, source) => ({ ...acc, [source.id]: false }),
      {}
    )
  );

  return (
    <CenterWrapper>
      {/*<SetupTab title="Setup" activeTab={"knowledge-sources"} />*/}
      <GeneralSetupTab
        title={"Setup"}
        pageType={"setup"}
        activeTab={"knowledge-sources"}
      />

      <div className="bg-white mb-8">
        <SectionHeader
          title="Knowledge Sources"
          description="These are used by your AI agent to learn how to respond to
              customers accurately."
        >
          <PrimaryButton
            type="button"
            onClick={() => setIsModalVisible(true)}
            startIcon={<PlusIcon className="size-6" />}
          >
            Add source
          </PrimaryButton>
        </SectionHeader>

        <div className="mt-10 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg relative overflow-hidden">
          {loading && <GlobalLoadingSpinner />}
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left sm:pl-6 text-gray-500 text-xs font-semibold leading-none"
                >
                  Content Type
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left sm:table-cell text-gray-500 text-xs font-semibold leading-none"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left sm:table-cell text-gray-500 text-xs font-semibold leading-none"
                >
                  Characters Used
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6" />
              </tr>
            </thead>

            <tbody>
              {(loading ? MOCK_UPLOADS.uploads : knowledgeSources).map(
                (source, index) => (
                  <KnowledgeSourceRow
                    key={source.id}
                    source={source}
                    handleCheckboxChange={handleCheckboxChange}
                    checkBoxes={checkBoxes}
                    renderIcon={renderIcon}
                    renderTitle={renderTitle}
                    index={index}
                    disableSource={disableSource}
                    enableSource={enableSource}
                    removeSource={removeSource}
                  />
                )
              )}

              <tr key="pagination-row" className="border-t text-sm">
                <td colSpan="100" className="p-4">
                  <PaginationRow
                    totalPages={totalPages}
                    currentPage={page}
                    setPage={setPage}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AddSourceModal
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        steps={steps}
      />

      <AddWebsiteModal
        isOpen={isAddWebsiteModalVisible}
        onClose={() => setIsAddWebsiteModalVisible(false)}
        goBack={() => {
          setIsModalVisible(true);
          setIsAddWebsiteModalVisible(false);
        }}
        onSuccess={fetchKnowledgeSources}
        api={api}
        setLoading={setLoading}
        loading={loading}
      />

      <AddQsAndAsModal
        isOpen={isQsAndAsModalVisible}
        onClose={() => setIsQsAndAsModalVisible(false)}
        goBack={() => {
          setIsModalVisible(true);
          setIsQsAndAsModalVisible(false);
        }}
        onSuccess={fetchKnowledgeSources}
        api={api}
        setLoading={setLoading}
      />

      <AddPoliciesModal
        isOpen={isPoliciesModalVisible}
        onClose={() => setIsPoliciesModalVisible(false)}
        goBack={() => {
          setIsModalVisible(true);
          setIsPoliciesModalVisible(false);
        }}
        onSuccess={fetchKnowledgeSources}
        api={api}
        setLoading={setLoading}
      />

      <AddGmailModal
        isOpen={isGmailModalVisible}
        onClose={() => setIsGmailModalVisible(false)}
        goBack={() => {
          setIsModalVisible(true);
          setIsGmailModalVisible(false);
        }}
        onSuccess={fetchKnowledgeSources}
        api={api}
        setLoading={setLoading}
        loading={loading}
      />

      <AddIntercomModal
        isOpen={isIntercomModalVisible}
        onClose={() => setIsIntercomModalVisible(false)}
        goBack={() => {
          setIsModalVisible(true);
          setIsIntercomModalVisible(false);
        }}
        onSuccess={fetchKnowledgeSources}
        api={api}
        setLoading={setLoading}
        loading={loading}
      />

      <AddUploadedFiles
        isOpen={isUploadFilesModalVisible}
        onClose={() => setIsUploadFilesModalVisible(false)}
        goBack={() => {
          setIsModalVisible(true);
          setIsUploadFilesModalVisible(false);
        }}
        onSuccess={fetchKnowledgeSources}
        api={api}
        setLoading={setLoading}
        loading={loading}
      />
    </CenterWrapper>
  );
};

export default KnowledgeSources;
